import React, { memo, useReducer } from "react"
import { useParams } from "react-router"

import { BrowserView, MobileView } from "components/helpers/hide"

import { PaginationNavigation } from "./components/pagination-navigation"
import { PaginationList } from "./components/pagination-list"

import { getPostsSlice } from "./helpers"

import { useMountedEffect } from "hooks/useMountedEffect"

const PAGINATION_DESKTOP_ITEMS_LIMIT = 7
const PAGINATION_DESKTOP_ITEMS_RANGE = 5

const PAGINATION_MOBILE_ITEMS_LIMIT = 5
const PAGINATION_MOBILE_ITEMS_RANGE = 3

type PaginationProps = {
  componentForLabelNext?: React.ReactNode
  componentForLabelPrev?: React.ReactNode
  onChange?: (value: number) => void
  onNextClick?: () => void
  onPrevClick?: () => void
  currentPage?: number
  pagesCount: number
}

type Action = { currentPage: number } | { cursorInList: number }
type State = {
  currentPage: number
  cursorInList: number
}

const Pagination: React.FC<PaginationProps> = ({
  componentForLabelNext,
  componentForLabelPrev,
  currentPage: page,
  onNextClick,
  onPrevClick,
  pagesCount,
  onChange
}) => {
  const { path } = useParams<{ path: string }>()
  const [{ currentPage, cursorInList }, setState] = useReducer(
    (prevState: State, newState: Action) => ({
      ...prevState,
      ...newState
    }),
    {
      currentPage: page ?? 1,
      cursorInList: page ? page - 1 : 0
    }
  )

  // check if filter was changed, reset current page on first page
  // we don't need to call it on initial render
  useMountedEffect(() => {
    if (!page) {
      return
    }

    // do not extra work if user already on first page
    if (currentPage === 1) {
      return
    }

    setState({
      currentPage: 1,
      cursorInList: 0
    })
    onChange?.(1)
  }, [path])

  const handleNextPageClick = () => {
    const isNotLastPage = cursorInList !== pagesCount

    setState({
      currentPage: currentPage + 1,
      cursorInList: isNotLastPage ? cursorInList + 1 : cursorInList
    })

    onNextClick?.()
  }

  const handlePrevPageClick = () => {
    const isNotFirstPage = cursorInList !== 0

    setState({
      currentPage: currentPage - 1,
      cursorInList: isNotFirstPage ? cursorInList - 1 : cursorInList
    })

    onPrevClick?.()
  }

  const handleDotsClick = () => {
    setState({
      cursorInList: cursorInList + 2,
      currentPage: currentPage + 2
    })

    onChange?.(currentPage + 2)
  }

  const handleLastItemClick = () => {
    // switch cursorInList on last item

    setState({
      cursorInList: pagesCount - 1,
      currentPage: pagesCount
    })

    onChange?.(pagesCount)
  }

  const handlePageClick = (index: number) => {
    const isNoFirstElement = index !== 0

    setState({
      currentPage: index + 1,
      cursorInList: isNoFirstElement ? index - 1 : index
    })

    onChange?.(index + 1)
  }

  // items for desktop view
  const { items, showDots, showLastItem, showPenultimateItem } = getPostsSlice({
    paginationLimit: PAGINATION_DESKTOP_ITEMS_LIMIT,
    range: PAGINATION_DESKTOP_ITEMS_RANGE,
    pagesCount,
    cursorInList
  })

  // items for mobile view
  const {
    items: itemsMobile,
    showDots: showDotsMobile,
    showLastItem: showLastItemMobile,
    showPenultimateItem: showPenultimateItemMobile
  } = getPostsSlice({
    paginationLimit: PAGINATION_MOBILE_ITEMS_LIMIT,
    range: PAGINATION_MOBILE_ITEMS_RANGE,
    pagesCount,
    cursorInList
  })

  const showNextBtn = currentPage !== pagesCount
  const showPrevBtn = currentPage !== 1

  return (
    <PaginationNavigation
      showNextBtn={showNextBtn}
      showPrevBtn={showPrevBtn}
      handlePrevPageClick={handlePrevPageClick}
      handleNextPageClick={handleNextPageClick}
      componentForLabelNext={componentForLabelNext}
      componentForLabelPrev={componentForLabelPrev}
    >
      <BrowserView>
        <PaginationList
          handleLastItemClick={handleLastItemClick}
          showPenultimateItem={showPenultimateItem}
          handlePageClick={handlePageClick}
          handleDotsClick={handleDotsClick}
          showLastItem={showLastItem}
          currentPage={currentPage}
          pagesCount={pagesCount}
          showDots={showDots}
          items={items}
        />
      </BrowserView>
      <MobileView>
        <PaginationList
          handleLastItemClick={handleLastItemClick}
          showPenultimateItem={showPenultimateItemMobile}
          handlePageClick={handlePageClick}
          handleDotsClick={handleDotsClick}
          showLastItem={showLastItemMobile}
          currentPage={currentPage}
          showDots={showDotsMobile}
          pagesCount={pagesCount}
          items={itemsMobile}
        />
      </MobileView>
    </PaginationNavigation>
  )
}

export default memo(Pagination)
